// Geometry helpers for the SVG charts.
//
// THE BOUNDARY THIS FILE GUARDS: format.ts says amounts arrive as decimal
// strings and are never parsed into a JS number for arithmetic, because every
// sum on this page was computed in Postgres where the type is numeric. Drawing
// a bar, however, needs a pixel height, and a pixel is a float no matter what.
//
// So parsing happens HERE and only here, and what comes out is only ever used
// to position a shape. Every number a human reads is still formatted from the
// original string by yen(). If those two ever disagree the label is right and
// the bar is wrong, which is the correct way round for a rounding error to
// fall.

/** Parse a decimal string for GEOMETRY ONLY. Never for a displayed figure. */
export function plotValue(value: string | null): number | null {
  if (value === null) return null
  // Number('') is 0, not NaN. Left unguarded that turns "we don't know" into a
  // bar sitting confidently on the zero line — the precise claim the AP column
  // refuses to make when no payroll ran.
  if (value.trim() === '') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

/**
 * Round an axis bound out to a readable step (1/2/5 × 10ⁿ).
 *
 * Axes stop on numbers people say out loud. A ¥3,847,221 maximum becomes ¥4M,
 * not ¥3.85M — the reader is judging proportions off the bars, and a gridline
 * they can hold in their head is worth more than a tight fit.
 */
export function niceBound(value: number): number {
  if (!Number.isFinite(value) || value <= 0) return 0
  const magnitude = 10 ** Math.floor(Math.log10(value))
  const normalised = value / magnitude
  const step = normalised <= 1 ? 1 : normalised <= 2 ? 2 : normalised <= 5 ? 5 : 10
  return step * magnitude
}

/**
 * Symmetric-about-zero domain for a series that crosses zero, so the zero line
 * sits where the eye expects and a −¥2M month is visibly twice a −¥1M one.
 */
export function domainFor(values: number[]): { min: number; max: number } {
  const finite = values.filter((value) => Number.isFinite(value))
  if (finite.length === 0) return { min: 0, max: 0 }
  const max = niceBound(Math.max(0, ...finite))
  const below = niceBound(Math.abs(Math.min(0, ...finite)))
  // `-0` is a real value in JS and compares unequal to 0 under Object.is, which
  // is enough to make a "did the domain reach zero" check behave oddly later.
  return { min: below === 0 ? 0 : -below, max }
}

/** Evenly spaced tick values across a domain, always including zero. */
export function ticksFor(min: number, max: number, count = 4): number[] {
  if (max === min) return [0]
  const step = (max - min) / count
  const ticks: number[] = []
  for (let i = 0; i <= count; i++) ticks.push(min + step * i)
  return ticks.some((tick) => Math.abs(tick) < 1e-9) ? ticks : [...ticks, 0].sort((a, b) => a - b)
}

/** Compact yen for axis ticks and dense labels: 1_250_000 → ¥1.3M. */
export function compactYen(value: number): string {
  const sign = value < 0 ? '-' : ''
  const magnitude = Math.abs(value)
  if (magnitude >= 1_000_000_000) return `${sign}¥${(magnitude / 1_000_000_000).toFixed(1)}B`
  if (magnitude >= 1_000_000) return `${sign}¥${(magnitude / 1_000_000).toFixed(1)}M`
  if (magnitude >= 1_000) return `${sign}¥${Math.round(magnitude / 1_000)}k`
  return `${sign}¥${Math.round(magnitude)}`
}

/** Map a value to a y pixel inside a plot box. */
export function yFor(value: number, min: number, max: number, top: number, height: number): number {
  if (max === min) return top + height
  return top + height - ((value - min) / (max - min)) * height
}

/**
 * Baseline for the slanted month labels under a plot. They are anchored at
 * their end and rotated -35°, so the text hangs down-left of this point; set
 * just under the plot, the longest "Mmm YYYY" still ends inside the viewBox
 * (48 units of bottom padding). Lower, it spills out of the SVG and over
 * whatever text sits under the chart.
 */
export function monthLabelY(plotBottom: number): number {
  return plotBottom + 12
}
